import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import ContextMenu from 'src/components/common/context_menu/ContextMenu';
import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import { IMenuItem } from 'src/components/common/types/contextMenuTypes';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import { IAnnotationLabel } from 'src/features/annotation_label/domain/types';
import {
	ANNOTATION_LABEL_LOADING_KEY,
	getAnnotationLabels,
} from 'src/features/annotation_label/store';
import { AppDispatch } from 'src/store';
import { RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

import CreateUpdateAnnotationLabelModal from './CreateUpdateAnnotationLabelModal';

export const AnnotationLabelTable: React.FC = () => {
	const { siteSlug } = useParams();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[ANNOTATION_LABEL_LOADING_KEY],
	);

	const [totalCount, setTotalCount] = useState<number>(0);
	const [queryState, setQueryState] = useState<ITableQueryState>({ page: 0 });
	const [selectedRow, setSelectedRow] = useState<IAnnotationLabel | null>(
		null,
	);
	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(
		null,
	);
	const [annotationLabels, setAnnotationLabels] = useState<
		IAnnotationLabel[]
	>([]);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	const menuItems: IMenuItem[] = [
		{
			label: 'Edit',
			icon: <ModeEditOutlinedIcon />,
			onClick: () => setIsModalOpen(true),
		},
	];

	const columns: IColumn<IAnnotationLabel>[] = [
		{
			key: 'name',
			label: 'Name',
			render: (data) => data.name,
		},
		{
			key: 'created_at',
			label: 'Created At',
			render: (data) => formatTimestamp(data.created_at),
		},
		{
			key: 'updated_at',
			label: 'Updated At',
			render: (data) => formatTimestamp(data.updated_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			align: 'left',
			render: (data) => (
				<IconButton
					onClick={(e) => {
						handleMenuClick(e, data);
					}}
				>
					<MoreVertOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleCloseModal = () => {
		setIsModalOpen(false);
		if (selectedRow) setSelectedRow(null);
	};

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		data: IAnnotationLabel,
	) => {
		setSelectedRow(data);
		setAnchorElement(event.currentTarget);
	};

	const fetchAnnotationLabels = () => {
		if (!siteSlug) return;
		const params = buildQueryParams(queryState);

		dispatch(
			getAnnotationLabels({
				params,
				siteSlug: siteSlug,
				onSuccess: (data) => {
					setTotalCount(data.info.count);
					setAnnotationLabels(data.results);
				},
			}),
		);
	};

	useEffect(() => {
		fetchAnnotationLabels();
	}, [queryState, siteSlug]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
			}}
		>
			<Box>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography
						variant='h6'
						sx={{
							fontSize: '20px',
							fontWeight: 500,
							color: 'rgba(0, 0, 0, 0.87)',
						}}
					>
						Annotation Labels
					</Typography>
					<Box sx={{ width: '231px', height: '36px' }}>
						<PrimaryButton
							variant='contained'
							color='primary'
							size='medium'
							onClick={() => {
								setIsModalOpen(true);
							}}
							disabled={isLoading}
						>
							ADD ANNOTATION LABEL
						</PrimaryButton>
					</Box>
				</Box>
			</Box>

			<DataTable
				columns={columns}
				data={annotationLabels}
				loadingKey={ANNOTATION_LABEL_LOADING_KEY}
				emptyMessage='No annotation label found'
				paginationInfo={{
					totalCount,
				}}
				queryState={queryState}
				onQueryChange={setQueryState}
			/>

			<CreateUpdateAnnotationLabelModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				initialData={selectedRow ?? undefined}
				onSuccess={() => {
					setQueryState({ page: 0 });
					handleCloseModal();
				}}
			/>

			<ContextMenu
				open={!!anchorElement}
				onClose={() => {
					setAnchorElement(null);
					setSelectedRow(null);
				}}
				anchorElement={anchorElement}
				clearAnchorElement={() => {
					setAnchorElement(null);
				}}
				items={menuItems}
			/>
		</Box>
	);
};
